import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CityPage = () => {
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    country: '',
    state: '',  
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');  

  const fetchCountries = async () => {
    try {
      const res = await axios.get('/admin/api/country');
      setCountries(res.data.countries || []);
    } catch (err) {
      setCountries([]);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const res = await axios.get(`/admin/api/state?country=${countryId}`);
      setStates(res.data.states || []);
    } catch (err) {
      setStates([]);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formValues.country) {
      fetchStates(formValues.country);
    } else {
      setStates([]);
      setFormValues((prev) => ({ ...prev, state: '' }));
    }
  }, [formValues.country]);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/api/city');
      setCities(res.data.cities || []);
    } catch (err) {
      console.error('Failed to fetch Cities:', err);
      toastError('Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      country: { required: true },
      state: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name,
        country: formValues.country,
        state: formValues.state,
      };

      if (editingId) {
        await axios.put(`/admin/api/city/${editingId}`, payload);
        toastSuccess('City updated successfully!');
      } else {
        await axios.post('/admin/api/city', payload);
        toastSuccess('City added successfully!');
      }
      fetchCities();
      handleCancel();
    } catch (err) {
      console.error('Failed to save City:', err);
      toastError(err.response?.data?.message || 'Failed to save City');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (city) => {
    setFormValues({
      name: city.name || '',
      country: city.country || '',
      state: city.state || '',
    });
    setEditingId(city._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await confirmDelete('Are you sure you want to delete this city?');
      if (!res.isConfirmed) return;
    } catch (err) { return; }
    try {
      await axios.delete(`/admin/api/city/${id}`);
      toastSuccess('City deleted successfully!');
      fetchCities();
    } catch (err) {
      console.error('Failed to delete City:', err);
      toastError('Failed to delete City');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', country: '', state: '' });
    setEditingId(null);
    setErrors({});
  };

  // Define table columns for CommonTable
  const tableColumns = [
    { key: 'srNo', label: 'Sr no.' },
    { key: 'name', label: 'Name' },
    { key: 'actions', label: 'Actions' }
  ];

  // Prepare data for CommonTable
  const filteredCities = cities.filter(city => 
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tableData = filteredCities.map((city, idx) => ({
    srNo: idx + 1,
    name: city.name,
    actions: (
      <div>
        <button
          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          title="Edit"
          onClick={() => handleEdit(city)}
        >
          <FaEdit size={14} />
        </button>
        <button
          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
          title="Delete"
          onClick={() => handleDelete(city._id)}
        >
          <FaTrash size={14} />
        </button>
      </div>
    )
  }));

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 mt-4">City</h3>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search Cities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '250px' }}
          />
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            + Add New City
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body">
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <CommonTable
          columns={tableColumns}
          data={tableData}
        />
      )}
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit City' : 'Add City'}
        fields={[
          {
            name: 'country',
            label: 'Country',
            type: 'select',
            options: countries.map(c => ({ value: c._id, label: c.name })),
            placeholder: 'Select Country'
          },
          {
            name: 'state',
            label: 'State',
            type: 'select',
            options: states.map(s => ({ value: s._id, label: s.name })),
            placeholder: 'Select State',
            disabled: !formValues.country
          },
          { 
            name: 'name', 
            label: 'City Name', 
            type: 'text',
            placeholder: 'Enter City Name'
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
        requiredFields={['name', 'country', 'state']}
        disabled={saving}
      />
    </div>
  );
};

export default CityPage;
